import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { emailTemplatesApi } from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import SendTestModal from '../components/SendTestModal'
import BrandingTab from '../components/BrandingTab'
import AssetsTab from '../components/AssetsTab'
import BatchRegenerateModal from '../components/BatchRegenerateModal'
import {
  PageHeader,
  Button,
  LoadingSpinner,
  EmptyState,
  ErrorMessage,
  ListContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Card,
  CardBody
} from '../components/common'

const TEMPLATE_CATEGORIES = {
  'Authentication': ['RegistrationConfirmation', 'EmailVerification', 'ForgotPassword', 'PasswordReset', 'PasswordChanged'],
  'Account': ['WelcomeToBuilding', 'AccountDeactivated', 'AccountReactivated', 'ProfileUpdated'],
  'Notifications': ['Announcement', 'DeliveryNotification', 'DeliveryReminder', 'AmenityBookingConfirmation', 'AmenityBookingCancellation', 'ParkingReservationConfirmation', 'ParkingReservationCancellation', 'ParkingCheckInExpired', 'JoinRequestApproved', 'JoinRequestRejected'],
  'System': ['MaintenanceNotification', 'SystemAlert']
}

const EmailTemplates = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [templateTypes, setTemplateTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSendTest, setShowSendTest] = useState(false)
  const [testingTemplate, setTestingTemplate] = useState(null)
  const [activeTab, setActiveTab] = useState('all-types')
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [variablesMap, setVariablesMap] = useState({})

  useEffect(() => {
    loadTemplates()
    loadTemplateTypes()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await emailTemplatesApi.list()
      setTemplates(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      setError(extractErrorMessage(err))
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const loadTemplateTypes = async () => {
    try {
      const response = await emailTemplatesApi.getTypes()
      const types = Array.isArray(response.data) ? response.data : []
      setTemplateTypes(types)
      loadAllVariables(types)
    } catch {
      setTemplateTypes([])
    }
  }

  const loadAllVariables = async (types) => {
    const results = await Promise.allSettled(
      types.map(type =>
        emailTemplatesApi.getVariablesByType(type.value).then(res => ({
          typeValue: type.value,
          variables: res.data?.variables || (Array.isArray(res.data) ? res.data : [])
        }))
      )
    )
    const map = {}
    for (const r of results) {
      if (r.status === 'fulfilled') {
        const vars = r.value.variables
        map[r.value.typeValue] = {
          total: vars.length,
          required: vars.filter(v => v.isRequired).length,
          optional: vars.filter(v => !v.isRequired).length
        }
      }
    }
    setVariablesMap(map)
  }

  const getTemplateTypeName = (value) => {
    const type = templateTypes.find(t => t.value === parseInt(value))
    return type ? type.name : `Type ${value}`
  }

  const isTemplateConfigured = (typeName) => {
    const type = templateTypes.find(t => t.name === typeName)
    if (!type) return false
    return templates.some(t => parseInt(t.templateType) === type.value)
  }

  const getTemplateByTypeName = (typeName) => {
    const type = templateTypes.find(t => t.name === typeName)
    if (!type) return null
    return templates.find(t => parseInt(t.templateType) === type.value)
  }

  const handleEditTemplate = (templateType) => {
    navigate(`/email-templates/edit/${templateType}`)
  }

  const handleCreateNew = () => {
    navigate('/email-templates/new')
  }

  const handleEditByTypeName = (typeName) => {
    const type = templateTypes.find(t => t.name === typeName)
    if (!type) return
    const existing = templates.find(t => parseInt(t.templateType) === type.value)
    if (existing) {
      navigate(`/email-templates/edit/${type.value}`)
    } else {
      navigate(`/email-templates/new?type=${type.value}`)
    }
  }

  const handleDeleteTemplate = async (templateType) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await emailTemplatesApi.delete(templateType)
        loadTemplates()
      } catch (err) {
        showError(err)
      }
    }
  }

  const handleSendTest = (template) => {
    setTestingTemplate(template)
    setShowSendTest(true)
  }

  const handleSendTestCancel = () => {
    setShowSendTest(false)
    setTestingTemplate(null)
  }

  const tabs = [
    { id: 'all-types', label: 'All Template Types' },
    { id: 'configured', label: `Configured Templates (${templates.length})` },
    { id: 'branding', label: 'Branding' },
    { id: 'assets', label: 'Assets' }
  ]

  if (loading && activeTab !== 'branding' && activeTab !== 'assets') {
    return <LoadingSpinner />
  }

  // Shared: categorized cards section
  const renderCategorizedCards = () => (
    <div className="space-y-8">
      {Object.entries(TEMPLATE_CATEGORIES).map(([category, typeNames]) => {
        const categoryTypes = typeNames.filter(name =>
          templateTypes.some(t => t.name === name)
        )
        if (categoryTypes.length === 0) return null

        return (
          <div key={category}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{category}</h3>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
              {categoryTypes.map(typeName => {
                const configured = isTemplateConfigured(typeName)
                const type = templateTypes.find(t => t.name === typeName)
                const varInfo = type ? variablesMap[type.value] : null
                return (
                  <Card
                    key={typeName}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleEditByTypeName(typeName)}
                  >
                    <CardBody>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{typeName}</h4>
                        <Badge variant={configured ? 'accent' : 'warning'} size="small">
                          {configured ? 'Configured' : 'Not Set'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        {typeName.replace(/([A-Z])/g, ' $1').trim()} email template
                      </p>
                      {varInfo && varInfo.total > 0 && (
                        <div className="flex gap-2">
                          {varInfo.required > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700">
                              {varInfo.required} required
                            </span>
                          )}
                          {varInfo.optional > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              {varInfo.optional} optional
                            </span>
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Uncategorized types */}
      {(() => {
        const categorized = new Set(Object.values(TEMPLATE_CATEGORIES).flat())
        const uncategorized = templateTypes.filter(t => !categorized.has(t.name))
        if (uncategorized.length === 0) return null
        return (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Other</h3>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
              {uncategorized.map(type => {
                const configured = templates.some(t => parseInt(t.templateType) === type.value)
                const varInfo = variablesMap[type.value]
                return (
                  <Card
                    key={type.name}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleEditByTypeName(type.name)}
                  >
                    <CardBody>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{type.name}</h4>
                        <Badge variant={configured ? 'accent' : 'warning'} size="small">
                          {configured ? 'Configured' : 'Not Set'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        {type.name.replace(/([A-Z])/g, ' $1').trim()} email template
                      </p>
                      {varInfo && varInfo.total > 0 && (
                        <div className="flex gap-2">
                          {varInfo.required > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700">
                              {varInfo.required} required
                            </span>
                          )}
                          {varInfo.optional > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              {varInfo.optional} optional
                            </span>
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          </div>
        )
      })()}
    </div>
  )

  // Shared: configured templates table
  const renderConfiguredTable = () => (
    <ListContainer>
      <div className="mb-4 flex justify-end">
        <Button variant="primary" onClick={handleCreateNew}>Create Template</Button>
      </div>
      {!Array.isArray(templates) || templates.length === 0 ? (
        <EmptyState
          message="No templates found"
          icon="📧"
          action={
            <Button variant="primary" onClick={handleCreateNew}>
              Create First Template
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell header>Template Type</TableCell>
              <TableCell header>Subject</TableCell>
              <TableCell header>Content</TableCell>
              <TableCell header>Status</TableCell>
              <TableCell header>
                <span className="sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.templateType} clickable onClick={() => handleEditTemplate(template.templateType)}>
                <TableCell>
                  <div className="text-sm font-medium text-gray-900">
                    {getTemplateTypeName(template.templateType)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Type ID: {template.templateType}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-900 max-w-xs truncate">
                    {template.subject || 'No subject'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-900">
                    {template.htmlBody ? `${template.htmlBody.length} chars` : 'No content'}
                  </div>
                  {template.textBody && (
                    <div className="text-xs text-gray-500">
                      Text: {template.textBody.length} chars
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={template.subject && template.htmlBody ? 'accent' : 'warning'} size="small">
                    {template.subject && template.htmlBody ? 'Complete' : 'Incomplete'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSendTest(template)
                      }}
                      title="Send test email"
                    >
                      Send Test
                    </Button>
                    <Button
                      variant="danger"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteTemplate(template.templateType)
                      }}
                      title="Delete template"
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </ListContainer>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Templates"
        subtitle="Manage email templates, branding, and assets"
        action={{
          label: "Batch Generate",
          onClick: () => setShowBatchModal(true)
        }}
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab 1: All Template Types */}
      {activeTab === 'all-types' && renderCategorizedCards()}

      {/* Tab 2: Configured Templates */}
      {activeTab === 'configured' && renderConfiguredTable()}

      {/* Tab 3: Branding */}
      {activeTab === 'branding' && <BrandingTab />}

      {/* Tab 4: Assets */}
      {activeTab === 'assets' && <AssetsTab />}

      {/* Send Test Email Modal */}
      {showSendTest && (
        <SendTestModal
          template={testingTemplate}
          templateTypeName={getTemplateTypeName(testingTemplate?.templateType)}
          onCancel={handleSendTestCancel}
        />
      )}

      {/* Batch Regenerate Modal */}
      {showBatchModal && (
        <BatchRegenerateModal
          templateTypes={templateTypes}
          existingTemplates={templates}
          onClose={() => setShowBatchModal(false)}
          onComplete={() => loadTemplates()}
        />
      )}
    </div>
  )
}

export default EmailTemplates
